import { Button, Form, message, Typography, Flex } from 'antd';
import { Auth } from 'aws-amplify';
import React from 'react';
import { useHistory, useLocation } from 'react-router';

import { IAuthProps } from '../../pages/Authentication';
import styles from './Auth.module.css';
import {
  CodeFormInput,
  EmailFormInput,
  PasswordFormInput,
  error,
  goto,
} from './utils';
import Logo from '../Logo';

const ForgotPassword: React.FC<IAuthProps> = ({
  form,
  authState,
  onAuthEvent,
  onStateChange,
  code,
}) => {
  const { pathname } = useLocation();
  const { push } = useHistory();
  const [email, setEmail] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [showCodeInput, setShowCodeInput] = React.useState<boolean>(false);

  const forgotPassword = React.useCallback(
    values => {
      setLoading(true);
      if (email.length > 0) {
        Auth.forgotPassword(email)
          .catch(err => error(err, authState, onAuthEvent))
          .finally(() => setLoading(false));
      } else {
        Auth.forgotPassword(values.email)
          .then(() => {
            setEmail(values.email);
            setShowCodeInput(true);
          })
          .catch(err2 => error(err2, authState, onAuthEvent))
          .finally(() => setLoading(false));
      }
    },
    [authState, email, onAuthEvent]
  );

  const forgotPasswordSubmit = React.useCallback(
    values => {
      setLoading(true);
      Auth.forgotPasswordSubmit(email, values.code, values.password)
        .then(() => {
          message.success('The password was successfully reset');
          onStateChange('signIn', {});
        })
        .catch(err2 => {
          setLoading(false);
          error(err2, authState, onAuthEvent);
        });
    },
    [authState, email, onStateChange, onAuthEvent]
  );

  return (
    <div className={styles.signUpForm}>
      <div style={{ width: '128px', margin: 'auto', marginBottom: '48px' }}>
        <Logo />
      </div>
      <Typography.Title
        style={{ paddingBottom: '16px', textAlign: 'center' }}
        level={4}
      >
        Reset Password
      </Typography.Title>
      <div style={{ marginBottom: 16 }}>
        {!showCodeInput
          ? "Please enter your email, and we'll send you a code to verify your account."
          : 'An email has been sent to your address with a 6-digit code. Please enter the code along with your new password to complete the password reset process.'}
      </div>

      <Form
        form={form}
        onFinish={showCodeInput ? forgotPasswordSubmit : forgotPassword}
        className="password-form"
        autoComplete={showCodeInput ? 'off' : 'on'}
        initialValues={{ code }}
        layout="vertical"
        requiredMark={false}
      >
        {showCodeInput ? (
          <>
            <Flex justify={'space-between'}>
              <CodeFormInput />
              <Button type={'link'} onClick={forgotPassword}>
                Resend Code
              </Button>
            </Flex>
            <PasswordFormInput newPassword />
          </>
        ) : (
          <EmailFormInput />
        )}

        <Flex vertical={true} align={'center'} gap={'large'}>
          <Form.Item>
            <Button
              loading={loading}
              type="primary"
              size="large"
              htmlType="submit"
            >
              {showCodeInput ? 'Submit' : 'Send Code'}
            </Button>
          </Form.Item>

          <Button
            type={'text'}
            onClick={goto('signIn', push, pathname, onStateChange)}
          >
            Back to Sign In
          </Button>
        </Flex>
      </Form>
    </div>
  );
};

export default ForgotPassword;
